import { Injectable } from '@angular/core';
import { BazisAuthService } from '@bazis/shared/services/auth.service';
import { combineLatest, pairwise, shareReplay, withLatestFrom } from 'rxjs';
import { ROLE, SHARE_REPLAY_SETTINGS } from '@app/configuration.service';
import { debounceTime, filter, startWith, switchMap, take, tap } from 'rxjs/operators';
import { ModalStartOnboardingComponent } from '@pages/shared/components/onboarding/modal-start-onboarding.component';
import { UntilDestroy } from '@ngneat/until-destroy';
import { getStand } from '@app/utils';

const AUTH_PAGE_URL = '/login';

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class LgAuthService extends BazisAuthService {
    private _isFirstRedirect = true;

    userEntityType = 'users.user';

    useOrganizationInfo = false;

    defaultPageRedirect$ = combineLatest([this.role$, this.organizationInfo$]).pipe(
        debounceTime(0),
        withLatestFrom(this.user$),
        startWith([null, null]),
        pairwise(),
        filter(([prevData, currentData]) => {
            const [prevRoleAndOrg, prevUser] = [...prevData];
            const [currentRoleAndOrg, currentUser] = [...currentData];
            return prevUser?.id !== currentUser?.id;
        }),
        tap(([prevData, currentData]) => {
            const [prevRoleAndOrg, prevUser] = [...prevData];
            const [currentRoleAndOrg, currentUser] = [...currentData];
            const regexp = /^\/(faq|about|legal\-documents){1}(\/.*)*$/;
            if (
                currentUser &&
                !currentUser.$snapshot.raw_password &&
                !regexp.test(location.pathname) &&
                currentUser.$snapshot.created_type_auth !== 'cert' &&
                currentUser.id !== prevUser?.id
            ) {
                this.router.navigate(['/login/init-password']);
                this._isFirstRedirect = false;
                return;
            }
            if (location.pathname.indexOf(AUTH_PAGE_URL) > -1 && !currentUser) return;
            this.redirectToDefaultPage(currentRoleAndOrg[0], currentRoleAndOrg[1], currentUser);
        }),
        shareReplay(SHARE_REPLAY_SETTINGS),
    );

    // redirect to default page
    redirectToDefaultPage(role, org, user) {
        switch (role) {
            case ROLE.courier:
                this.router.navigate(['/profile/personal-info']);
                break;
            case ROLE.anonymous:
            case ROLE.base:
                // do not redirect from public pages to user-initial
                const regexp = /^\/(faq|about|legal\-documents|referral){1}(\/.*)*$/;
                if (!regexp.test(location.pathname)) {
                    this.router.navigate(['/login']);
                }
                break;
            default:
                if (location.pathname.indexOf('access-denied') > -1 && !this._isFirstRedirect) {
                    this.router.navigate(['/']);
                } else if (location.pathname.indexOf(AUTH_PAGE_URL) > -1) {
                    if (user.$snapshot.roles.length === 1) {
                        if (this.urlToRedirectAfterAuth && user.$snapshot.is_onboarding_viewed) {
                            this.router.navigateByUrl(this.urlToRedirectAfterAuth);
                        } else {
                            this.router.navigate(['/objects']);
                        }
                        this.urlToRedirectAfterAuth = null;
                    } else this.router.navigate(['/login/select-role']);
                }

                if (
                    !user.$snapshot.is_onboarding_viewed &&
                    org?.$snapshot.status?.id === 'organization_active'
                ) {
                    this.startOnboarding();
                }
        }
        this._isFirstRedirect = false;
    }

    authRequest$() {
        return this.srv.commonGetRequest$('authing/auth');
    }

    startOnboarding() {
        this.modalService.create({
            component: ModalStartOnboardingComponent,
            backdropClose: false,
            hasCloseIcon: false,
            cssClass: 'tms-modal_onboarding',
        });
    }

    setOnboardingViewed() {
        if (getStand() === 'dev') return;
        this.userId$
            .pipe(
                switchMap((userId) =>
                    this.srv
                        .saveEntity$(
                            this.userEntityType,
                            userId,
                            this.srv.generateEntityBody(this.userEntityType, userId, {
                                is_onboarding_viewed: true, // true
                            }),
                        )
                        .pipe
                        // take(1),
                        // tap((response) => {
                        //     console.log('[DEV] response', response);
                        // }),
                        (),
                ),
            )
            .subscribe();
    }

    afterLogoutRedirect() {
        this.router.navigate(['/login']);
    }
}
